import { Link } from 'react-router-dom';

import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';
import { SIN_CIFRA, esNegativo } from '../logica/agregacion.js';
import {
  ESTADO_DE_TARJETA, leerDisponibleDelPresupuesto, leerSaldosDeCuentas, leerTotalesDelMes,
  resolverEstadoDeTarjeta,
} from '../logica/estadosHonestos.js';
import { CifraCargando, CifraConError } from './EstadoDeTarjeta.jsx';

/*
 * T-5 · LAS TRES CIFRAS DE CABECERA (RF-210, D-310, D-314, RN-611 a RN-613).
 *
 * Tres cifras de lectura rápida, cada una con el enlace a la pantalla donde está
 * su detalle. NINGUNA exige cálculo nuevo en el servidor y ninguna se recalcula
 * aquí: son la misma cifra que muestra su pantalla de origen y es defecto que
 * difieran (RN-600).
 *
 *   1. Saldo de tus cuentas · una cifra POR MONEDA, nunca sumadas (D-310). La
 *      deuda de las tarjetas NO se resta: esto es «cuánto dinero tengo».
 *   2. Gasto de <mes> · la MISMA cifra «Gastos» del resumen del mes (RN-612).
 *   3. Disponible de tu presupuesto · `totalDisponible` tal cual, que puede ser
 *      negativo y se muestra con su signo (RN-613, D-146).
 *
 * FORMATO ÚNICO DE IMPORTE en las tres (RNF-113): «$1,234.56 MXN», código al
 * final, con la implementación única del núcleo. Aquí no se formatea a mano.
 *
 * APILADAS Y NO EN FILA (S-14-02, RNF-114): a 320 px tres cifras en fila no caben
 * sin recortar un importe. Cambia la disposición, jamás el dato ni su moneda.
 *
 * ===========================================================================
 * REESCRITO EN EL FIX DEL 2026-10-08 · ESTADOS HONESTOS (D-323, RN-622 a RN-624)
 * ===========================================================================
 * ANTES: las tres cifras no tenían estados. Pintaban «—» mientras cargaban y
 * resolvían el fallo cambiando una NOTA al pie, en el mismo renglón donde decían
 * «aún no tienes cuentas». Un error de lectura y una ausencia real se veían casi
 * igual, y el defecto de producción —«Aún no tienes ninguna cuenta activa» con 15
 * cuentas en la respuesta— se pintó por ahí.
 *
 * AHORA: cada cifra pasa por `resolverEstadoDeTarjeta` y está en exactamente UNO
 * de los cuatro estados, con su propio «Reintentar» que relanza SOLO su consulta.
 * El texto de vacío de cada cifra vive en un único sitio y SOLO puede aparecer en
 * el estado VACIO (RN-623).
 *
 * LÍMITE DE ESTE FIX: la TARJETA de presupuesto (T-2) no se toca — su respuesta
 * dice la verdad. Lo que sí entra es la CIFRA «Disponible de tu presupuesto», que
 * es de T-5: conserva palabra por palabra su veredicto de «aún no defines
 * presupuesto» y lo único que gana es no pintarlo mientras carga ni cuando falló.
 */

/**
 * Una cifra EN ESTADO (c) o (d): con datos, o con su vacío de verdad.
 * Los estados (a) y (b) los pintan `CifraCargando` y `CifraConError`.
 */
function Cifra({
  rotulo, valores, nota, enlace, textoDelEnlace, alerta = false, dato, vacia,
}) {
  return (
    <Link className="cifra" to={enlace} data-prueba={dato}>
      <span className="rot">{rotulo}</span>

      {vacia
        ? <span className="val" data-prueba={`${dato}-sin-dato`}>{SIN_CIFRA}</span>
        : valores.map((valor, indice) => (
          <span
            key={valor.clave}
            className={`val${indice > 0 ? ' segunda' : ''}${alerta ? ' alerta' : ''}`}
            data-prueba={`${dato}-valor`}
            data-moneda={valor.codigoISO}
          >
            {valor.texto}
          </span>
        ))}

      {nota && <span className="nota3">{nota}</span>}
      <span className="ir">{textoDelEnlace}</span>
    </Link>
  );
}

export function CabeceraDeTresCifras({ nombreDelMes, cuentas, movimientos, presupuesto }) {
  /* 1 · SALDO DE TUS CUENTAS (RN-611) ----------------------------------
     La fuente es `totalesPorMoneda` de ConsultarCuentas, que el servidor arma
     SOLO con cuentas activas (RN-072d). El lector es el que decide si la
     respuesta se pudo leer: una 200 con una forma que el contrato no tiene va a
     ERROR, no a «no tienes cuentas» (RN-622b). */
  const saldo = resolverEstadoDeTarjeta(cuentas, leerSaldosDeCuentas);
  const saldosParaPintar = (saldo.valor ?? []).map((renglon) => ({
    clave: renglon.idMoneda,
    codigoISO: renglon.codigoISO,
    texto: formatearImporte(renglon.totalSaldos, renglon.codigoISO),
  }));

  /* 2 · GASTO DEL MES (RN-612) ----------------------------------------- */
  const gasto = resolverEstadoDeTarjeta(movimientos, leerTotalesDelMes);
  const gastosParaPintar = (gasto.valor ?? []).map((total) => ({
    clave: total.idMoneda,
    codigoISO: total.codigoISO,
    texto: formatearImporte(total.totalGastos, total.codigoISO),
  }));

  /* 3 · DISPONIBLE DEL PRESUPUESTO (RN-613) ---------------------------- */
  const disponible = resolverEstadoDeTarjeta(presupuesto, leerDisponibleDelPresupuesto);
  const disponibleEnNegativo = esNegativo(disponible.valor);
  const disponibleParaPintar = disponible.valor === null || disponible.valor === undefined
    ? []
    : [{ clave: 'MXN', codigoISO: 'MXN', texto: formatearImporte(disponible.valor, 'MXN') }];

  return (
    <div className="cab3" data-prueba="cabecera-de-tres-cifras">
      {/* --- 1 · Saldo de tus cuentas ----------------------------------- */}
      {saldo.estado === ESTADO_DE_TARJETA.CARGANDO && (
        <CifraCargando rotulo="Saldo de tus cuentas" dato="cifra-de-saldo" />
      )}
      {saldo.estado === ESTADO_DE_TARJETA.ERROR && (
        <CifraConError
          rotulo="Saldo de tus cuentas"
          mensaje="No se pudo cargar el saldo de tus cuentas."
          error={cuentas.error}
          alReintentar={cuentas.recargar}
          dato="cifra-de-saldo"
        />
      )}
      {(saldo.estado === ESTADO_DE_TARJETA.VACIO || saldo.estado === ESTADO_DE_TARJETA.CON_DATOS) && (
        <Cifra
          rotulo="Saldo de tus cuentas"
          valores={saldosParaPintar}
          vacia={saldo.estado === ESTADO_DE_TARJETA.VACIO}
          nota={saldo.estado === ESTADO_DE_TARJETA.VACIO
            ? 'Aún no tienes ninguna cuenta activa.'
            : `${saldosParaPintar.length > 1 ? 'Cada moneda va por separado y nunca se suman. ' : ''}`
              + 'Lo que debes en tus tarjetas no se resta aquí.'}
          /* RN-611 / CA-14-27 · el vacío DE VERDAD lleva acceso a crear; con datos,
             al detalle de siempre. Dos estados distintos, dos destinos distintos. */
          enlace={saldo.estado === ESTADO_DE_TARJETA.VACIO ? '/cuentas/nueva' : '/cuentas'}
          textoDelEnlace={saldo.estado === ESTADO_DE_TARJETA.VACIO
            ? 'Crear mi primera cuenta →'
            : 'Ver cuentas y tarjetas →'}
          dato="cifra-de-saldo"
        />
      )}

      {/* --- 2 · Gasto del mes ----------------------------------------- */}
      {gasto.estado === ESTADO_DE_TARJETA.CARGANDO && (
        <CifraCargando rotulo={`Gasto de ${nombreDelMes}`} dato="cifra-de-gasto" />
      )}
      {gasto.estado === ESTADO_DE_TARJETA.ERROR && (
        <CifraConError
          rotulo={`Gasto de ${nombreDelMes}`}
          mensaje={`No se pudo cargar tu gasto de ${nombreDelMes}.`}
          error={movimientos.error}
          alReintentar={movimientos.recargar}
          dato="cifra-de-gasto"
        />
      )}
      {(gasto.estado === ESTADO_DE_TARJETA.VACIO || gasto.estado === ESTADO_DE_TARJETA.CON_DATOS) && (
        <Cifra
          rotulo={`Gasto de ${nombreDelMes}`}
          valores={gastosParaPintar}
          vacia={gasto.estado === ESTADO_DE_TARJETA.VACIO}
          nota={gasto.estado === ESTADO_DE_TARJETA.VACIO
            ? `Todavía no hay movimientos en ${nombreDelMes}.`
            : 'De una compra a meses solo cuenta la mensualidad de este mes.'}
          enlace="/movimientos"
          textoDelEnlace="Ver todos los movimientos →"
          dato="cifra-de-gasto"
        />
      )}

      {/* --- 3 · Disponible de tu presupuesto -------------------------- */}
      {disponible.estado === ESTADO_DE_TARJETA.CARGANDO && (
        <CifraCargando rotulo="Disponible de tu presupuesto" dato="cifra-de-disponible" />
      )}
      {disponible.estado === ESTADO_DE_TARJETA.ERROR && (
        <CifraConError
          rotulo="Disponible de tu presupuesto"
          mensaje="No se pudo cargar lo que te queda de tu presupuesto."
          error={presupuesto.error}
          alReintentar={presupuesto.recargar}
          dato="cifra-de-disponible"
        />
      )}
      {(disponible.estado === ESTADO_DE_TARJETA.VACIO
        || disponible.estado === ESTADO_DE_TARJETA.CON_DATOS) && (
        <Cifra
          rotulo="Disponible de tu presupuesto"
          valores={disponibleParaPintar}
          vacia={disponible.estado === ESTADO_DE_TARJETA.VACIO}
          nota={disponible.estado === ESTADO_DE_TARJETA.VACIO
            ? `Aún no defines presupuesto para ${nombreDelMes}.`
            : disponibleEnNegativo
              ? `Te pasaste de lo que habías presupuestado para ${nombreDelMes}.`
              : `Lo que te queda de lo presupuestado para ${nombreDelMes}.`}
          enlace="/presupuesto"
          textoDelEnlace="Ver el presupuesto →"
          alerta={disponibleEnNegativo}
          dato="cifra-de-disponible"
        />
      )}
    </div>
  );
}
